import { useNavigate } from 'react-router-dom';
import type { Strategy } from '@/types/strategy';

interface StrategyCardProps {
  strategy: Strategy;
  isActive: boolean;
  onToggleActive: (id: string) => void;
  selectedAccountId?: string | null;
}

export default function StrategyCard({ strategy, isActive, onToggleActive, selectedAccountId }: StrategyCardProps) {
  const navigate = useNavigate();
  const navUrl = `/strategies/${strategy.id}${selectedAccountId ? `?accountId=${selectedAccountId}` : ''}`;

  return (
    <>
      <div className="bg-background-100 rounded-xl border border-background-200/70 hover:border-background-300/60 transition-all duration-200 group">
        <div
          onClick={() => navigate(navUrl)}
          className="p-4 md:p-5 cursor-pointer"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <h3 className="text-sm font-heading font-semibold text-foreground-900 truncate">{strategy.name}</h3>
                {strategy.type === 'trailingStop' && (
                  <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase tracking-wide">
                    <i className="ri-stop-mini-line text-[9px]"></i>
                    Trailing
                  </span>
                )}
                {strategy.type === 'chin' && (
                  <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-accent-500/10 text-accent-400 border border-accent-500/20 uppercase tracking-wide">
                    <i className="ri-radar-line text-[9px]"></i>
                    Chin
                  </span>
                )}
                {strategy.type === 'autochinbot' && (
                  <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-primary-500/10 text-primary-400 border border-primary-500/20 uppercase tracking-wide">
                    <i className="ri-robot-2-line text-[9px]"></i>
                    AutoChin
                  </span>
                )}
                <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-400 animate-pulse' : 'bg-foreground-300'}`}></span>
              </div>
            </div>

            <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {strategy.type !== 'trailingStop' && strategy.type !== 'chin' && strategy.type !== 'autochinbot' && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(navUrl); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-400 hover:bg-primary-500/10 transition-all cursor-pointer"
                  title="Edit strategy"
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Toggle switch - outside the click-to-navigate area */}
        <div className="px-4 md:px-5 pb-3.5 flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onToggleActive(strategy.id)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 cursor-pointer ${
              isActive ? 'bg-emerald-500' : 'bg-background-300'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded-full bg-background-50 transition-transform duration-200 ${
                isActive ? 'translate-x-[18px]' : 'translate-x-[3px]'
              }`}
            ></span>
          </button>
        </div>
      </div>
    </>
  );
}